import { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, formatApiError, tokenStore } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Banknote, CreditCard, ExternalLink, FileText, Loader2, Plus, Receipt,
  Smartphone, Trash2, Wallet, X,
} from "lucide-react";
import { toast } from "sonner";

const METHOD_LABEL = {
  stripe: { label: "Stripe", Icon: CreditCard },
  cash: { label: "Kontant", Icon: Banknote },
  card_terminal: { label: "Kortterminal", Icon: CreditCard },
  vipps: { label: "Vipps", Icon: Smartphone },
  bank_transfer: { label: "Bankoverføring", Icon: Wallet },
  other: { label: "Annet", Icon: Wallet },
};
const CURRENCY_SYMBOL = { EUR: "€", NOK: "kr" };
const DEFAULT_VAT = 25;

const emptyForm = () => ({
  amount: "",
  currency: "EUR",
  method: "stripe",
  description: "",
  vat_rate: DEFAULT_VAT,
  paid_at: new Date().toISOString().slice(0, 10),
  reference: "",
});

/**
 * Admin panel to register received payments and generate a receipt PDF
 * for the accountant. Payments included in a receipt are locked and
 * shown in green; unbilled payments can be edited / removed freely.
 */
export default function BookingPaymentsCard({ booking, canEdit = true }) {
  const [payments, setPayments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // payment being edited
  const [form, setForm] = useState(emptyForm());
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptNote, setReceiptNote] = useState("");

  const load = async () => {
    if (!booking?.id) return;
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get(`/bookings/${booking.id}/payments`),
        api.get(`/bookings/${booking.id}/receipts`),
      ]);
      setPayments(p.data || []);
      setReceipts(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [booking?.id]);

  const totals = useMemo(() => {
    const t = {};
    for (const p of payments) {
      const cur = p.currency;
      const entry = t[cur] || { gross: 0, net: 0, vat: 0, count: 0 };
      entry.gross += p.amount;
      entry.net += p.net_amount || 0;
      entry.vat += p.vat_amount || 0;
      entry.count += 1;
      t[cur] = entry;
    }
    return t;
  }, [payments]);

  const unbilled = payments.filter((p) => !p.receipt_id);
  const hasUnbilled = unbilled.length > 0;

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    if (p.receipt_id) {
      toast.error("Denne betalingen er låst i en generert kvittering.");
      return;
    }
    setEditing(p);
    setForm({
      amount: String(p.amount),
      currency: p.currency,
      method: p.method,
      description: p.description || "",
      vat_rate: p.vat_rate ?? DEFAULT_VAT,
      paid_at: p.paid_at || new Date().toISOString().slice(0, 10),
      reference: p.reference || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const amount = parseFloat(String(form.amount).replace(",", "."));
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error("Ugyldig beløp");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Beskrivelse er påkrevd");
      return;
    }
    const body = {
      amount,
      currency: form.currency,
      method: form.method,
      description: form.description.trim(),
      vat_rate: parseFloat(String(form.vat_rate).replace(",", ".")) || 0,
      paid_at: form.paid_at,
      reference: form.reference.trim() || null,
    };
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/bookings/${booking.id}/payments/${editing.id}`, body);
        toast.success("Betaling oppdatert");
      } else {
        await api.post(`/bookings/${booking.id}/payments`, body);
        toast.success("Betaling lagt til");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Slett betalingen «${p.description}»?`)) return;
    setBusy(true);
    try {
      await api.delete(`/bookings/${booking.id}/payments/${p.id}`);
      toast.success("Slettet");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const generateReceipt = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.id}/receipts`, {
        note: receiptNote.trim() || null,
      });
      toast.success(`Kvittering ${r.data.receipt_number} generert`);
      setReceiptOpen(false);
      setReceiptNote("");
      await load();
      const url = `${BACKEND_URL}/api/receipts/${r.data.id}/pdf?auth=${encodeURIComponent(tokenStore.get())}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteReceipt = async (r) => {
    if (!window.confirm(`Slette kvittering ${r.receipt_number}? Betalingene blir låst opp så du kan generere på nytt.`)) return;
    setBusy(true);
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success("Slettet");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openReceiptPdf = (r) => {
    const url = `${BACKEND_URL}/api/receipts/${r.id}/pdf?auth=${encodeURIComponent(tokenStore.get())}`;
    window.open(url, "_blank");
  };

  const fmt = (a, cur) => `${a.toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${CURRENCY_SYMBOL[cur] || cur}`;

  return (
    <Card className="p-6" data-testid="booking-payments-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Receipt className="w-5 h-5 text-[#A57F39]" />
            <h2 className="text-lg font-semibold text-[#0C1824]">
              Faktura / Kvittering
            </h2>
          </div>
          <p className="text-xs text-[#2B3F4D] max-w-lg">
            Registrer alle fakturalinjer for denne turen. Du kan endre og slette
            linjer fritt inntil du <b>godkjenner og genererer PDF</b>. Etter godkjenning
            låses linjene og du får ett permanent kvitteringsnummer per booking.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openAdd}
            data-testid="add-payment-button"
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Legg til linje
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-[#A57F39]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laster…
        </div>
      ) : payments.length === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg">
          Ingen fakturalinjer registrert for denne turen ennå.
        </div>
      ) : (
        <div className="space-y-2" data-testid="payments-list">
          {payments.map((p) => {
            const meta = METHOD_LABEL[p.method] || METHOD_LABEL.other;
            const Icon = meta.Icon;
            const locked = !!p.receipt_id;
            return (
              <div
                key={p.id}
                data-testid={`payment-row-${p.id}`}
                className={`flex items-center gap-3 rounded-lg border p-3 ${
                  locked
                    ? "border-emerald-200 bg-emerald-50/50"
                    : "border-slate-200 bg-white hover:border-[#BD984D]/40"
                }`}
              >
                <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                  locked ? "bg-emerald-100 text-emerald-700" : "bg-[#BD984D]/15 text-[#A57F39]"
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[#0C1824] truncate">
                      {p.description}
                    </div>
                    {locked && (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">
                        {p.receipt_number}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-[#2B3F4D] flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                    <span>{meta.label}</span>
                    <span>·</span>
                    <span>{p.paid_at}</span>
                    <span>·</span>
                    <span>MVA {p.vat_rate}%</span>
                    {p.reference && (
                      <>
                        <span>·</span>
                        <span className="font-mono truncate">ref {p.reference}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-mono font-bold text-[#0C1824] tabular-nums">
                    {fmt(p.amount, p.currency)}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    netto {fmt(p.net_amount || 0, p.currency)}
                  </div>
                </div>
                {canEdit && !locked && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`payment-edit-${p.id}`}
                      className="text-xs text-[#1DA3B3] hover:underline px-2 py-1"
                    >
                      Rediger
                    </button>
                    <button
                      onClick={() => remove(p)}
                      data-testid={`payment-delete-${p.id}`}
                      className="text-slate-500 hover:text-red-600 p-1"
                      aria-label="Slett"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Totals per currency */}
      {Object.keys(totals).length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {Object.entries(totals).map(([cur, t]) => (
            <div
              key={cur}
              className="rounded-lg bg-slate-50 border border-slate-200 p-3"
              data-testid={`payment-total-${cur}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-[#A57F39] font-semibold">
                Sum i {cur} · {t.count} betaling{t.count === 1 ? "" : "er"}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-1 text-sm">
                <div>
                  <div className="text-[10px] text-slate-500">Netto</div>
                  <div className="font-mono">{fmt(t.net, cur)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">MVA</div>
                  <div className="font-mono">{fmt(t.vat, cur)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Brutto</div>
                  <div className="font-mono font-bold text-[#0C1824]">
                    {fmt(t.gross, cur)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Receipts */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#A57F39]" />
            <h3 className="text-sm font-semibold text-[#0C1824]">
              Godkjent kvittering
            </h3>
          </div>
          {canEdit && hasUnbilled && receipts.length === 0 && (
            <Button
              size="sm"
              onClick={() => setReceiptOpen(true)}
              data-testid="generate-receipt-button"
              className="bg-[#0C1824] hover:bg-[#152238] text-white"
            >
              <Receipt className="w-4 h-4 mr-1.5" />
              Godkjenn og generer PDF ({unbilled.length})
            </Button>
          )}
        </div>
        {receipts.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 text-center">
            Ingen kvittering godkjent ennå. Alle linjer over kan endres.
          </div>
        ) : (
          <div className="space-y-2" data-testid="receipts-list">
            {receipts.map((r) => (
              <div
                key={r.id}
                data-testid={`receipt-row-${r.id}`}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
              >
                <div className="h-10 w-10 rounded-full bg-[#0C1824] text-white flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#0C1824]">
                    Kvittering {r.receipt_number}
                  </div>
                  <div className="text-xs text-[#2B3F4D]">
                    Utstedt {new Date(r.issued_at).toLocaleString("nb-NO", {
                      year: "numeric", month: "2-digit", day: "2-digit",
                      hour: "2-digit", minute: "2-digit",
                    })} · {r.issued_by_name || "kontoret"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {Object.entries(r.totals || {}).map(([cur, t]) => (
                      <span key={cur} className="mr-3">
                        {fmt(t.gross, cur)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => openReceiptPdf(r)}
                    data-testid={`receipt-open-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#1DA3B3] hover:underline px-2 py-1"
                  >
                    Åpne PDF <ExternalLink className="w-3 h-3" />
                  </button>
                  {r.drive_view_url && (
                    <a
                      href={r.drive_view_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#1DA3B3] hover:underline px-2 py-1"
                    >
                      I Drive <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {canEdit && (
                    <button
                      onClick={() => deleteReceipt(r)}
                      data-testid={`receipt-delete-${r.id}`}
                      className="text-slate-500 hover:text-red-600 p-1"
                      aria-label="Slett kvittering"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Rediger linje" : "Legg til fakturalinje"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Beskrivelse *</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="F.eks. Leie 5 dager · Sunliner"
                data-testid="payment-form-description"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label>Beløp (brutto, inkl. MVA) *</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  data-testid="payment-form-amount"
                  placeholder="F.eks. 2500 eller -500 for refusjon"
                />
                <p className="text-[11px] text-[#2B3F4D] mt-1">
                  Bruk minus foran beløpet for refusjon eller tilbakeført depositum (f.eks. −500).
                </p>
              </div>
              <div>
                <Label>Valuta</Label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  data-testid="payment-form-currency"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="NOK">NOK (kr)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Metode</Label>
                <select
                  value={form.method}
                  onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}
                  data-testid="payment-form-method"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  {Object.entries(METHOD_LABEL).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>MVA-sats (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={form.vat_rate}
                  onChange={(e) => setForm((f) => ({ ...f, vat_rate: e.target.value }))}
                  data-testid="payment-form-vat"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Betalt dato</Label>
                <Input
                  type="date"
                  value={form.paid_at}
                  onChange={(e) => setForm((f) => ({ ...f, paid_at: e.target.value }))}
                  data-testid="payment-form-date"
                />
              </div>
              <div>
                <Label>Referanse (valgfritt)</Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                  placeholder="pi_… / txn-id"
                  data-testid="payment-form-reference"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="payment-form-submit"
              className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? "Oppdater" : "Lagre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt confirm dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Godkjenn og generer kvittering</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#2B3F4D]">
              {unbilled.length} fakturalinje{unbilled.length === 1 ? "" : "r"} tas
              med på kvitteringen. Etter godkjenning låses linjene og du får ett
              permanent kvitteringsnummer. PDF-en speiles automatisk til Google Drive.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              Vil du endre beløp senere må du slette kvitteringen og generere på nytt.
            </p>
            <div>
              <Label>Kommentar (valgfritt)</Label>
              <Textarea
                rows={2}
                value={receiptNote}
                onChange={(e) => setReceiptNote(e.target.value)}
                placeholder="F.eks. «Depositum returnert kontant ved retur»"
                data-testid="receipt-form-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={generateReceipt}
              disabled={busy}
              data-testid="receipt-form-submit"
              className="bg-[#0C1824] hover:bg-[#152238] text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Generer PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
